import { api } from '@/lib/api.js';

// GET /api/tracon/phf/:phfId/submission — what the pilot actually submitted,
// read from pilot_history_forms.raw_submission (prompt 69 item 4).
//
// NOT the same thing as the contact peek's "Copy PHF", which builds from the
// synced CRM Contact. Seven of the disclosure fields do not exist as fields on
// the Contacts module, so anything sourced from there returns undefined for
// them on every contact in the book.
//
// Keyed on the PHF id, never the PHF token: the token is a live client
// credential and a broker-only surface must not be reachable by one.
// A broker correction to one field of a submitted PHF. raw_submission is
// immutable; this overlays the typed column and records why.
export function correctPhfField(phfId, body) {
  return api.patch(`/api/tracon/phf/${encodeURIComponent(phfId)}/field`, body);
}

// GET /api/tracon/answer-lists: the one list for each fixed answer (utils/answer-lists). Fetched once per page load.
let listsPromise = null;
export function getAnswerLists() {
  if (!listsPromise) listsPromise = api.get('/api/tracon/answer-lists').then((r) => (r && r.lists) || {}).catch((e) => { listsPromise = null; throw e; });
  return listsPromise;
}

export function getPhfSubmission(phfId) {
  return api.get(`/api/tracon/phf/${encodeURIComponent(phfId)}/submission`);
}

// GET /api/tracon/phf/:phfId/documents — every document filed against this
// pilot (across terms, by contact) plus the filable type list.
export function getPhfDocuments(phfId) {
  return api.get(`/api/tracon/phf/${encodeURIComponent(phfId)}/documents?_t=${Date.now()}`);
}

// POST /api/tracon/phf/:phfId/documents — multipart, raw fetch with the JWT
// (the shared api wrapper hardcodes JSON; same pattern as licenses.service).
// document_type is required: the server refuses a file with no stated kind.
export async function uploadPhfDocument(phfId, file, documentType) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('document_type', documentType);
  const res = await fetch(`/api/tracon/phf/${encodeURIComponent(phfId)}/documents?_t=${Date.now()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
    body: fd,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error(body?.error || `upload failed (HTTP ${res.status})`);
  }
  return body;
}

// GET /api/tracon/phf/:phfId/signed-pdf?append=<doc ids>: the signed PHF,
// made on request: the pilot's answers, the Tower stamp when his signature
// covers them as they stand, and the chosen documents appended. Raw fetch with
// the JWT (a blob, never a link the browser opens without the header). The
// server's warnings ride X-Tracon-Warnings so the sheet can toast each one.
export async function fetchSignedPhfPdf(phfId, appendIds = []) {
  const token = localStorage.getItem('tracon_token');
  const q = appendIds.length ? `append=${encodeURIComponent(appendIds.join(','))}&` : '';
  const res = await fetch(`/api/tracon/phf/${encodeURIComponent(phfId)}/signed-pdf?${q}_t=${Date.now()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b?.error || `signed PHF ${res.status}`);
  }
  let warnings = [];
  try { warnings = JSON.parse(decodeURIComponent(res.headers.get('X-Tracon-Warnings') || '%5B%5D')); } catch { warnings = ['The server sent warnings this page could not read.']; }
  return { blob: await res.blob(), standing: res.headers.get('X-Phf-Signature') || 'unknown', warnings: Array.isArray(warnings) ? warnings : [] };
}
